import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useDistributorSettingsContext } from '../context/DistributorSettingsContext';
import { PutProductDistributorFulfillmentType } from '../../ProductSettings/hooks/useFetchProductSettingsDistributorOverrides';
import { SkuFulfillmentMethod } from '../../../Enums/SkuFulfillmentMethod';
import { toYyyyMmDdInput, isSunday, maxInMarketDate, getEffectiveInMarketDate } from '../../ProductSettings/utils/inMarketDate';

export const SkuInMarketDatePicker = ({ row, handleUpdateRow }) => {
    const { showInMarketDateError } = useDistributorSettingsContext();
    const isReplenishable = row.skuFulfillmentType?.id === SkuFulfillmentMethod.REPLENISHABLE;
    const isOrderOnly = row.skuFulfillmentType?.id === SkuFulfillmentMethod.ORDER_ONLY;
    // No geography layer here, so the floor is simply this SKU's own default in-market date.
    const itemInMarketDate = toYyyyMmDdInput(row.inMarketDateDefault);
    const itemEndInMarketDate = toYyyyMmDdInput(row.endInMarketDateDefault);
    // The SKU must have its own default in-market date before a distributor-level override can be set.
    const disabled = row?.legal === false || (!isReplenishable && !isOrderOnly) || !itemInMarketDate;
    const value = getEffectiveInMarketDate(row);
    const min = itemInMarketDate;
    const weekLimit = maxInMarketDate();
    // Tightest upper bound: the item's own end in-market date (a start cannot fall after the end) and
    // the 52-week limit (date strings sort chronologically).
    const max = [itemEndInMarketDate, weekLimit].filter(Boolean).sort()[0];

    const handleChange = async (newValue) => {
        let apiDate = null;
        if (newValue !== null) {
            if (!newValue.isValid()) return;
            const selected = newValue.format('YYYY-MM-DD');
            // Guard against typed input that bypasses the disabled calendar days.
            if (!isSunday(selected)) {
                showInMarketDateError('In-market date must be a Sunday.');
                return;
            }
            if (min && selected < min) return;
            if (selected > max) return;
            apiDate = selected;
        }

        const updatedRow = {
            ...row,
            inMarketDateOverride: apiDate ? `${apiDate}T00:00:00` : null,
            inMarketDateIsDefault: !apiDate
        };

        const result = await PutProductDistributorFulfillmentType(
            row.product,
            { ...updatedRow, id: row.distributorId, skuFulfillmentType: row.skuFulfillmentType },
            null,
            { setInMarketDate: true, inMarketDate: apiDate }
        );
        if (result.ok) {
            await handleUpdateRow(updatedRow);
        } else {
            showInMarketDateError(result.error || 'The in-market date could not be saved.');
        }
    };

    return (
        <DatePicker
            value={value ? dayjs(value) : null}
            disabled={disabled}
            onChange={handleChange}
            minDate={min ? dayjs(min) : undefined}
            maxDate={dayjs(max)}
            shouldDisableDate={(date) => date.day() !== 0}
            slotProps={{
                textField: { size: 'small', fullWidth: true },
                field: { clearable: true }
            }}
        />
    );
};

SkuInMarketDatePicker.propTypes = {
    row: PropTypes.object,
    handleUpdateRow: PropTypes.func
};
